import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuthStore } from '@/stores/auth';
import { agents, type Agent } from '@/lib/api';

interface EditAgentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: Agent | null;
}

interface FormData {
  name: string;
  email: string;
  phone: string;
  specialization: string;
  accepts_leads: boolean;
  max_leads_per_day: number | undefined;
}

export function EditAgentModal({ open, onOpenChange, agent }: EditAgentModalProps) {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    specialization: '',
    accepts_leads: true,
    max_leads_per_day: undefined,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormData, string>>>({});

  // Update form data when agent changes
  useEffect(() => {
    if (agent) {
      setFormData({
        name: agent.name || '',
        email: agent.email || '',
        phone: agent.phone || '',
        specialization: agent.specialization || '',
        accepts_leads: agent.accepts_leads ?? true,
        max_leads_per_day: agent.max_leads_per_day,
      });
    }
  }, [agent]);

  const updateMutation = useMutation({
    mutationFn: (data: Partial<Agent>) => agents.update(token!, agent!.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      handleClose();
    },
  });

  const handleClose = () => {
    setErrors({});
    onOpenChange(false);
  };

  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof FormData, string>> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nafn er nauðsynlegt';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Netfang er nauðsynlegt';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Netfang er ekki gilt';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !agent) {
      return;
    }

    const submitData: Partial<Agent> = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      phone: formData.phone.trim() || undefined,
      specialization: formData.specialization.trim() || undefined,
      accepts_leads: formData.accepts_leads,
      max_leads_per_day: formData.max_leads_per_day || undefined,
    };

    updateMutation.mutate(submitData);
  };

  const handleChange = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  if (!agent) return null;

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Breyta söluaðila</DialogTitle>
          <DialogDescription>
            Breyttu upplýsingum um söluaðila. Reitir með stjörnu eru nauðsynlegir.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="edit-name">
              Nafn <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-name"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Jón Jónsson"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-500">{errors.name}</p>
            )}
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label htmlFor="edit-email">
              Netfang <span className="text-red-500">*</span>
            </Label>
            <Input
              id="edit-email"
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              placeholder="jon@fasteignir.is"
              className={errors.email ? 'border-red-500' : ''}
            />
            {errors.email && (
              <p className="text-sm text-red-500">{errors.email}</p>
            )}
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label htmlFor="edit-phone">Sími</Label>
            <Input
              id="edit-phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="+354 555 1234"
            />
          </div>

          {/* Specialization */}
          <div className="space-y-2">
            <Label htmlFor="edit-specialization">Sérhæfing</Label>
            <Input
              id="edit-specialization"
              value={formData.specialization}
              onChange={(e) => handleChange('specialization', e.target.value)}
              placeholder="t.d. Íbúðir, Einbýlishús, Atvinnuhúsnæði"
            />
            <p className="text-xs text-gray-500">
              Tegund eigna sem söluaðili sérhæfir sig í
            </p>
          </div>

          {/* Accepts leads toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-gray-900 text-sm">Tekur við ábendingum</p>
              <p className="text-xs text-gray-500">
                Ábendingum er úthlutað á söluaðila
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleChange('accepts_leads', !formData.accepts_leads)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                formData.accepts_leads ? 'bg-prima-600' : 'bg-gray-200'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  formData.accepts_leads ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Max leads per day */}
          {formData.accepts_leads && (
            <div className="space-y-2">
              <Label htmlFor="edit-max_leads_per_day">Hámark ábendinga á dag</Label>
              <Input
                id="edit-max_leads_per_day"
                type="number"
                min="1"
                max="100"
                value={formData.max_leads_per_day || ''}
                onChange={(e) =>
                  handleChange(
                    'max_leads_per_day',
                    e.target.value ? parseInt(e.target.value, 10) : undefined
                  )
                }
                placeholder="Ótakmarkað"
              />
              <p className="text-xs text-gray-500">
                Skildu eftir autt fyrir engin takmörk
              </p>
            </div>
          )}

          {/* Error message from API */}
          {updateMutation.isError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm text-red-800">
                {updateMutation.error instanceof Error
                  ? updateMutation.error.message
                  : 'Villa kom upp við að uppfæra söluaðila'}
              </p>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="outline" onClick={handleClose}>
              Hætta við
            </Button>
            <Button type="submit" disabled={updateMutation.isPending}>
              {updateMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              Vista breytingar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
